import React, { useEffect, useState, useRef } from "react";
import useDebouncedCallback from "hooks/useDebouncedCallback";
import { compileMarkupPreview } from "requests/cms";
import { phoenixProjectId } from "components/CMS/ContentManagementList";
import LoadingCover from "./LoadingCover";

type ChangeEditAction = "startEditing" | "save" | "cancel";

export interface MarkdownEditorState {
  markdown?: string;
  setMarkdown?: (markdown: string) => void;
  compiled: string;
  isEditing?: boolean,
  changeEditing?: (action: ChangeEditAction) => void,
  isShowingHelp?: boolean,
  setIsShowingHelp?: (isShowingHelp: boolean) => void,
};

interface Props extends MarkdownEditorState {
  
}

// Component is split into the main component, and a hook that manages its state
// This allows the parent component to have full access to the components state while not needing to have complex logic/duplicated code to maintain it
export const useMarkdownEditor = (initialMarkdown: string, initialMarkup: string): MarkdownEditorState => {
  const [markdown, setMarkdown] = useState<string>(initialMarkdown);
  const [compiled, setCompiled] = useState<string>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isShowingHelp, setIsShowingHelp] = useState<boolean>(false);

  const mounted = useRef(true);
  
  const updateMarkdownDebounced = useDebouncedCallback(compileMarkupPreview, 2000, 5000);

  useEffect(() => () => {
    mounted.current = false;
  }, []);

  useEffect(() => {
    setMarkdown(initialMarkdown);
  }, [initialMarkdown]);

  useEffect(() => {
    if (isEditing) updateMarkdownDebounced(phoenixProjectId, markdown, markdownResponse => setCompiled(markdownResponse.markup), mounted);
  }, [markdown, isEditing]);

  const changeEditing = (action: ChangeEditAction) => {
    switch (action) {
      case "startEditing":
        setIsEditing(true);
        break;
      case "save":
        setIsEditing(false);
        setCompiled(null);
        setIsShowingHelp(false);
        break;
      case "cancel":
        setIsEditing(false);
        setCompiled(null);
        setMarkdown(initialMarkdown);
        setIsShowingHelp(false);
        break;
    }
  };

  return {
    markdown,
    setMarkdown,
    compiled: compiled ?? initialMarkup,
    isEditing,
    changeEditing,
    isShowingHelp,
    setIsShowingHelp,
  };
};

const MarkdownEditor = ({
  markdown,
  setMarkdown,
  compiled,
  isEditing,
  isShowingHelp,
}: Props) => {

  const [localMarkdown, setLocalMarkdown] = useState<string>("");

  useEffect(() => {
    if (isEditing)
      setLocalMarkdown(markdown);
    else
      setLocalMarkdown("");
  }, [isEditing]);

  const setMarkdownDebounced = useDebouncedCallback((s: string) => setMarkdown(s), 500);

  const onChangeWrapped: React.ChangeEventHandler<HTMLTextAreaElement> = (event) => {
    const value = event.target.value;
    setLocalMarkdown(value);
    setMarkdownDebounced(value); // Update markdown state from parent
  };

  return (
    <div className="markdown-editor">
      {(compiled == null || compiled === undefined) && <LoadingCover loadingMessages={["Loading Contents..."]} />}
      {isShowingHelp && <MarkdownHelp />}
      {isEditing && <textarea value={localMarkdown} onChange={onChangeWrapped} />}
      <div className="rendered" dangerouslySetInnerHTML={{ __html: compiled ?? "" }} />
    </div>
  );
};

export default MarkdownEditor;

const markdownFormatGuide =
`Markdown Format Guide

# Heading 1
###### H6

**Bold**  *Italic*

- Bullet point list item
- Another item
  - Sub-item 

1. Numbered/Ordered list item
2. Another item

sample code for an image (using its key from the Content File Manager)
@blue_square`;

export const MarkdownHelp = () => {
  const markdownRef = useRef<HTMLDivElement>(null);
  const renderedRef = useRef<HTMLDivElement>(null);
  const isSyncingRef = useRef(false); // need to track that we're syncing otherwise changing the scroll of another container will fire onscroll again

  const syncScroll = (source: HTMLElement, target: HTMLElement) => {
    const percentage = source.scrollTop / (source.scrollHeight - source.clientHeight);
    target.scrollTop = percentage * (target.scrollHeight - target.clientHeight);
  };

  const handleScroll = (e: React.UIEvent<HTMLElement>) => {
    if (!markdownRef.current || !renderedRef.current || isSyncingRef.current) return;

    isSyncingRef.current = true;
    if (e.target === markdownRef.current) {
      syncScroll(markdownRef.current, renderedRef.current);
    } else if (e.target === renderedRef.current) {
      syncScroll(renderedRef.current, markdownRef.current);
    }

    setTimeout(() => { isSyncingRef.current = false; }, 0);
  };

  return (
    <div className="markdown-help">
      <div ref={markdownRef} onScroll={handleScroll} style={{ whiteSpace: "pre-wrap" }}>
        {markdownFormatGuide}
      </div>
      <div ref={renderedRef} onScroll={handleScroll}>
        {/* Markdown taken from phoenix endpoint so it is accurate to what they should see, besides the harcoded blue_square image */}
        <div style={{ fontFamily: "Helvetica, Arial, sans-serif;" }}><p>Markdown Format Guide</p>
          <h1>Heading 1</h1>
          <h6>H6</h6>
          <p>
            <strong>Bold</strong>
            <em>Italic</em>
          </p>
          <ul>
            <li>Bullet point list item</li>
            <li>Another item
            <ul>
              <li>Sub-item</li>
            </ul>
            </li>
          </ul>
          <ol>
            <li>Numbered/Ordered list item</li>
            <li>Another item</li>
          </ol>
          <p>sample code for an image (using its key from the Content File Manager)</p>
          <img src="data:image/svg+xml,%3Csvg%20width%3D%22100%22%20height%3D%22100%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Crect%20width%3D%22100%22%20height%3D%22100%22%20fill%3D%22blue%22/%3E%3C/svg%3E" alt="Blue Square" />
        </div>
      </div>
    </div>
  );
};
